import { useState, type FC } from "react";
import Button from "../UI/Button";
import { useTranslation } from "@/i18n";
import styles from "./HowToReadModal.module.css";
import { StepTitleDescription } from "../StepTitleDescritpion";
import { Tab, type TabItem } from "../UI/Tab";
import { GlobalPrescriptionTable } from "@/components/GlobalPrescriptionTable";
import { useContactLensesStore } from "@/store";

interface HowToReadModalProps {
  onClose: () => void;
  watch: any;
  setValue?: any;
  control: any;
  register: any;
}

interface PrescriptionSection {
  title?: string | null;
  descriptions: string[];
}

interface PrescriptionCard {
  cardTitle: string;
  attributeKey?: string;
  sections: PrescriptionSection[];
}

const PrescriptionCard: FC<{ card: PrescriptionCard }> = ({ card }) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h4 className={styles.cardHeaderTitle}>{card.cardTitle}</h4>
      </div>
      <div className={styles.cardBody}>
        {card.sections.map((section, index) => (
          <div key={index} className={styles.cardSection}>
            {section.title && (
              <h5 className={styles.cardSectionTitle}>{section.title}</h5>
            )}
            <div className={styles.cardSectionContent}>
              {section.descriptions.map((description, descIndex) => (
                <p key={descIndex} className={styles.cardDescription}>
                  {description}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PrescriptionTable: FC<{ card: PrescriptionCard }> = ({ card }) => {
  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableSections}>
        {card.sections.map((section, index) => (
          <div key={index} className={styles.tableSection}>
            {section.title && (
              <h5 className={styles.tableSectionTitle}>{section.title}</h5>
            )}
            {section.descriptions.map((desc, descIndex) => (
              <p key={descIndex} className={styles.tableDescription}>
                {desc}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const HowToReadModal: FC<HowToReadModalProps> = ({
  watch,
  control,
  register,
  onClose,
}) => {
  const { t } = useTranslation();
  const { contactLensesData } = useContactLensesStore();

  const samePrescription = watch("samePrescription");

  const [selectedTab, setSelectedTab] = useState<string>("0");

  // Get all cards
  const allCards: PrescriptionCard[] = t("modal.howToRead.cards", {
    returnObjects: true,
  }) as PrescriptionCard[];

  // Build a set of attribute keys from OD and OS products (no duplicates)
  const attributeKeys = new Set([
    "EYE",
    ...Object.keys(contactLensesData?.OD?.product?.attributes || {}),
    ...Object.keys(contactLensesData?.OS?.product?.attributes || {}),
  ]);

  const filteredAndOrderedCards: PrescriptionCard[] = [];

  attributeKeys.forEach((key) => {
    const cardForAttribute = allCards.find((card) => card.attributeKey === key);
    if (cardForAttribute) {
      filteredAndOrderedCards.push(cardForAttribute);
    }
  });

  const tabs: TabItem[] = filteredAndOrderedCards.map((card, index) => ({
    id: index.toString(),
    label: card.cardTitle,
    content: null,
  }));

  const selectedCard = filteredAndOrderedCards[parseInt(selectedTab)];

  const highlightedAttribute = selectedCard?.attributeKey || null;

  console.log("WATCH HW: ", selectedCard);

  return (
    <div className={styles.container}>
      <StepTitleDescription
        title={t("modal.howToRead.title")}
        description={t("modal.howToRead.description")}
        variant="howToReadModal"
      />

      {/* Desktop: tabs + table */}
      <div className={styles.tabPanel}>
        <Tab tabs={tabs} defaultTab="0" onSelectTab={setSelectedTab} />
        {selectedCard && (
          <>
            <PrescriptionTable card={selectedCard} />
            <div className={styles.prescriptionTableWrapper}>
              <GlobalPrescriptionTable
                contactLensesData={contactLensesData}
                control={control}
                register={register}
                samePrescription={samePrescription}
                disableRemove={true}
                modal={true}
                highlightedAttribute={highlightedAttribute}
                showCheckbox={false}
              />
            </div>
          </>
        )}
      </div>

      {/* Mobile: cards only */}
      <div className={styles.cardList}>
        {allCards?.map((card, index) => (
          <PrescriptionCard key={index} card={card} />
        ))}
      </div>

      <Button onClick={onClose} variant="secondary" className={styles.button}>
        {t("modal.howToRead.goBack")}
      </Button>
    </div>
  );
};
